import { Bus, GraduationCap, Route, Smartphone, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { siteConfig } from "@/config/siteConfig";
import busInterior from "@/assets/comfort.png";

const iconMap = {
  Bus,
  GraduationCap,
  Route,
  Smartphone,
};

export function FeaturesSection() {
  return (
    <section id="features" className="section-padding bg-secondary/30 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary) / 0.1) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 animate-fade-in-up">
          <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            What We Offer
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Experience<span className="text-gradient"> Excellence</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            From seamless booking to border crossing, we've perfected every detail of your cross-border journey.
          </p>
        </div>

        {/* Features Grid */}
        {(() => {
          const visibleFeatures = siteConfig.features.filter(feature => {
            if (feature.title === "Luggage Services" && !siteConfig.luggageServicesEnabled) {
              return false;
            }
            return true;
          });
          
          const gridColsClass = 
            visibleFeatures.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4';
          
          return (
            <div className={`grid md:grid-cols-2 ${gridColsClass} gap-6 mb-16`}>
              {visibleFeatures.map((feature, index) => {
                const Icon = iconMap[feature.icon as keyof typeof iconMap] || Bus;
                const isExternal = feature.path?.startsWith('http');
                const Component = isExternal ? 'a' : Link;
                const componentProps = isExternal
                  ? { href: feature.path, target: '_blank', rel: 'noopener noreferrer' }
                  : { to: feature.path || '#' };
                
                return (
                  <Component
                    key={feature.title}
                    {...componentProps}
                    className="route-card p-6 group cursor-pointer animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:neon-glow transition-all duration-300">
                    <Icon className="w-8 h-8 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-display font-semibold text-xl mb-3 text-foreground group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {feature.description}
                </p>

                {/* Link */}
                <div className="flex items-center gap-2 text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn More
                  <Info className="w-4 h-4" />
                </div>
              </Component>
            );
          })}
            </div>
          );
        })()}

        {/* Feature Image */}
        <div className="relative rounded-3xl overflow-hidden animate-fade-in-up animation-delay-400">
          {/* Mobile: Stacked Layout */}
          <div className="md:hidden flex flex-col">
            <img
              src={busInterior}
              alt="Luxury bus interior"
              className="w-full h-[300px] object-cover"
            />
            <div className="bg-gradient-to-b from-primary/5 to-background p-6">
              <h3 className="font-display text-2xl font-bold mb-4 text-foreground">
                Experience Comfort Like Never Before
              </h3>
              <p className="text-foreground mb-6 opacity-90">
                Our coaches feature spacious seating, climate control, and premium amenities for your comfort throughout the journey.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {siteConfig.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-2 px-3 py-2 rounded-full glass text-xs md:text-sm bg-primary/10">
                    <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-foreground">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop: Overlay Layout */}
          <div className="hidden md:block relative">
            <img
              src={busInterior}
              alt="Luxury bus interior"
              className="w-full h-[450px] lg:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent" />
            
            <div className="absolute inset-0 flex items-center p-8 lg:p-16">
              <div className="max-w-lg">
                <h3 className="font-display text-2xl lg:text-3xl font-bold mb-4 text-foreground">
                  Experience Comfort Like Never Before
                </h3>
                <p className="text-foreground mb-6 opacity-90">
                  Our coaches feature spacious seating, climate control, and premium amenities for your comfort throughout the journey.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {siteConfig.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2 px-4 py-2 rounded-full glass text-sm">
                      <span className="w-2 h-2 rounded-full bg-primary" />
                      {amenity}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
